import { type RefObject } from "react";
import * as Popover from "@radix-ui/react-popover";
import { keyframes, styled } from "@pigment-css/react";
import { CheckmarkThin } from "@acto/web-common/components";
import { useTranslations } from "@acto/web-common/locales";
import { toVirtualRef } from "@acto/taskpane/field-sales-2/utils/toVirtualRef";
import { SHEET_Z_INDEX } from "@acto/taskpane/field-sales-2/components/primitives/BottomSheet";
import { InitialsAvatar } from "@acto/taskpane/field-sales-2/components/primitives/InitialsAvatar";
import { Skeleton } from "@acto/taskpane/field-sales-2/components/primitives/Skeleton";
import { User } from "@acto/taskpane/core/domain";
import { useAssignableUsers } from "@acto/taskpane/field-sales-2/data-access/todos/useAssignableUsers";

const ANCHOR_GAP_PX = 8;
const MAX_HEIGHT_PX = 240;

const popoverIn = keyframes({
  from: { opacity: 0, transform: "scale(0.96)" },
  to: { opacity: 1, transform: "scale(1)" },
});

const popoverOut = keyframes({
  from: { opacity: 1, transform: "scale(1)" },
  to: { opacity: 0, transform: "scale(0.96)" },
});

const PopoverContent = styled(Popover.Content)(({ theme }) => ({
  // Must sit above SHEET_Z_INDEX (see BottomSheet.tsx) — the picker opens from inside a sheet, so a
  // lower z-index renders it behind the sheet surface where it's invisible and unclickable.
  zIndex: SHEET_Z_INDEX + 1,
  width: "16rem",
  maxHeight: `${MAX_HEIGHT_PX}px`,
  overflowY: "auto",
  padding: `${theme.fieldSalesTwo.spacing[10]} ${theme.fieldSalesTwo.spacing[8]} ${theme.fieldSalesTwo.spacing[8]}`,
  borderRadius: theme.fieldSalesTwo.radius[32],
  background: theme.fieldSalesTwo.colors.surface.glassStrong,
  border: `1px solid ${theme.fieldSalesTwo.colors.surface.glassBorderStrong}`,
  backdropFilter: `blur(${theme.fieldSalesTwo.effects.blurSm})`,
  WebkitBackdropFilter: `blur(${theme.fieldSalesTwo.effects.blurSm})`,
  boxShadow: theme.fieldSalesTwo.shadows.glass,
  display: "flex",
  flexDirection: "column",
  transformOrigin: "top",
  outline: "none",
  '&[data-state="open"]': {
    animation: `${popoverIn} 150ms cubic-bezier(0.22, 1, 0.36, 1)`,
  },
  '&[data-state="closed"]': {
    animation: `${popoverOut} 120ms ease`,
    pointerEvents: "none",
  },
}));

const Row = styled("button")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[16],
  width: "100%",
  minHeight: "2.625rem",
  textAlign: "left",
  border: "none",
  background: "transparent",
  borderRadius: theme.fieldSalesTwo.radius[16],
  cursor: "pointer",
  padding: `0 ${theme.fieldSalesTwo.spacing[8]}`,
  WebkitTapHighlightColor: "transparent",
  transition: "background 120ms ease",
  "&:hover": { background: theme.fieldSalesTwo.colors.surface.glassMedium },
  "&:active": { background: theme.fieldSalesTwo.colors.surface.glassStrong },
}));

const RowName = styled("span")(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[520],
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.subtle,
  color: theme.fieldSalesTwo.colors.neutral[900],
}));

const SelectedMark = styled("span")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  flexShrink: 0,
  color: theme.fieldSalesTwo.colors.accent.action,
  "& svg": { width: theme.fieldSalesTwo.spacing[20], height: theme.fieldSalesTwo.spacing[20] },
}));

const Message = styled("p")(({ theme }) => ({
  margin: 0,
  padding: theme.fieldSalesTwo.spacing[16],
  textAlign: "center",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size.base,
  color: theme.fieldSalesTwo.colors.neutral[600],
}));

const SkeletonRow = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[16],
  minHeight: "2.625rem",
  padding: `0 ${theme.fieldSalesTwo.spacing[8]}`,
}));

const SKELETON_COUNT = 3;

function AssigneeRowSkeleton() {
  return (
    <SkeletonRow aria-hidden="true">
      <Skeleton.Circle size="1.75rem" />
      <Skeleton.Line width="55%" height="0.875rem" />
    </SkeletonRow>
  );
}

interface AssigneePickerSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** The trigger button this popover is anchored to. */
  anchorRef: RefObject<HTMLElement | null>;
  clientId?: string;
  selectedId?: string;
  onSelect: (user: User) => void;
}

/**
 * Compact popover anchored to the assignee toggle it was opened from — not a full-screen sheet.
 * There is no search: the assignable list is a client's small team, not a searchable directory.
 */
export function AssigneePickerSheet({
  isOpen,
  onOpenChange,
  anchorRef,
  clientId,
  selectedId,
  onSelect,
}: AssigneePickerSheetProps) {
  const t = useTranslations("fieldSalesTwo.assigneePicker");
  const { users, isFetching } = useAssignableUsers(isOpen ? clientId : undefined);

  const showSkeletons = isFetching && users.length === 0;

  return (
    <Popover.Root open={isOpen} onOpenChange={onOpenChange} modal={false}>
      <Popover.Anchor virtualRef={toVirtualRef(anchorRef)} />
      <Popover.Portal>
        <PopoverContent
          side="bottom"
          align="end"
          sideOffset={ANCHOR_GAP_PX}
          role="menu"
          aria-orientation="vertical"
          aria-label={t("title")}
          updatePositionStrategy="always"
          onInteractOutside={(e) => {
            const target = e.target as Node | null;
            if (target && anchorRef.current?.contains(target)) {
              e.preventDefault();
            }
          }}
        >
          {showSkeletons ? (
            Array.from({ length: SKELETON_COUNT }).map((_, i) => <AssigneeRowSkeleton key={`init-${i}`} />)
          ) : users.length > 0 ? (
            users.map((user) => {
              const isSelected = user.id === selectedId;
              return (
                <Row
                  key={user.id}
                  type="button"
                  role="menuitemradio"
                  aria-checked={isSelected}
                  onClick={() => {
                    onSelect(user);
                    onOpenChange(false);
                  }}
                >
                  <InitialsAvatar name={user.displayName ?? ""} size="1.75rem" />
                  <RowName>{user.displayName}</RowName>
                  {isSelected && (
                    <SelectedMark aria-label={t("selectedAriaLabel")}>
                      <CheckmarkThin />
                    </SelectedMark>
                  )}
                </Row>
              );
            })
          ) : (
            <Message>{t("empty")}</Message>
          )}
        </PopoverContent>
      </Popover.Portal>
    </Popover.Root>
  );
}
